import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GraduationCap, Eye, EyeOff } from 'lucide-react';
import { API_BASE_URL } from '../services/api';

const TeacherLoginPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ usernameOrEmail: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/tutors/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to login');
      localStorage.setItem('userInfo', JSON.stringify(data));
      if (data.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-200px)] px-6 py-12">
      <div className="w-full max-w-md bg-white/5 border border-white/10 p-8 rounded-[40px] backdrop-blur-xl">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-purple-500/10 border border-purple-500/20 rounded-full text-purple-400 text-xs font-bold uppercase tracking-wider mb-4">
            Teacher Login
          </div>
          <h2 className="text-3xl font-bold text-white flex items-center justify-center gap-2">
            <GraduationCap size={28} className="text-purple-400" /> Welcome Back
          </h2>
          <p className="text-gray-400 text-sm mt-2">Use the credentials provided by your admin</p>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500 text-red-400 p-3 rounded-xl mb-6 text-sm text-center">
            {error}
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-5">
          <div>
            <label htmlFor="usernameOrEmail" className="block text-sm font-medium text-gray-400 mb-2">Email or Username</label>
            <input
              type="text"
              id="usernameOrEmail"
              placeholder="email@example.com or username"
              onChange={handleChange}
              value={formData.usernameOrEmail}
              className="w-full bg-darkBg border border-white/10 p-3.5 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none text-sm transition-all"
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-400 mb-2">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                placeholder="••••••••"
                onChange={handleChange}
                value={formData.password}
                className="w-full bg-darkBg border border-white/10 p-3.5 pr-12 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none text-sm transition-all"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div className="flex justify-end text-sm">
            <Link to="/forgot-password?role=teacher" className="font-medium text-purple-400 hover:underline text-xs">
              Forgot password?
            </Link>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3.5 bg-purple-600 hover:bg-purple-500 text-white rounded-2xl font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Logging in...' : 'Login as Teacher'}
          </button>
        </form>

      </div>
    </div>
  );
};

export default TeacherLoginPage;
